import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";
import {useEffect, useState} from "react";
import type {MathBlockElement} from "slate";
import {ReactEditor, useSlate, type RenderElementProps} from "slate-react";

import {addStyle} from "@khanacademy/wonder-blocks-core";
import {Popover, PopoverContentCore} from "@khanacademy/wonder-blocks-popover";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {MathJax} from "@khan/component-tex";
import {isKhanmigoEditing} from "@khan/guide-math-editor";

import {shouldHighlightNodePath} from "../helpers/decoration-helpers.ts";
import {updateBlockMathNode} from "../helpers/math-helpers.ts";
import {isSelectionWithinNode} from "../helpers/node-helpers.ts";

const StyledDiv = addStyle("div");
const StyledTextarea = addStyle("textarea");

export default function MathBlockNode(
    props: RenderElementProps,
): React.ReactElement {
    const {element, attributes, children} = props;

    const editor = useSlate();
    const math = element as MathBlockElement;
    const [editedTeX, setEditedTeX] = useState(math.tex || "");

    const path = ReactEditor.findPath(editor, element);
    const selectionWithin = isSelectionWithinNode(editor, element);
    const showKhanmigoHighlight = shouldHighlightNodePath(editor, path);

    useEffect(() => {
        updateBlockMathNode(editor, editedTeX);
    }, [editor, editedTeX]);

    // When rendering void elements, Slate needs to put the ref on a
    // DOM node, hence the <div> wrapper. Also, we need to render the
    // children somewhere within the void, even though they are empty:
    // https://github.com/ianstormtaylor/slate/issues/3930
    const div = (
        <StyledDiv>
            {children}
            <MathJax
                style={[
                    styles.math,
                    selectionWithin && styles.focused,
                    showKhanmigoHighlight && styles.selected,
                ]}
                contextForAnalytics="ai-guide"
                noLog
            >
                {math.tex || ""}
            </MathJax>
        </StyledDiv>
    );

    if (!selectionWithin || isKhanmigoEditing(editor)) {
        // If selection/cursor isn't within this node, we just render the
        // TeX from above to skip the unnecessary rendering of the closed
        // popover.
        return (
            <StyledDiv
                {...attributes}
                style={[styles.wrapper]}
                // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- TODO: Address a11y error
                tabIndex={0}
                data-math-content={math.tex || ""}
                contentEditable={false}
            >
                <StyledDiv style={styles.inlineBlock}>{div}</StyledDiv>
            </StyledDiv>
        );
    }

    const numRows = editedTeX.split("\n").length;

    return (
        <StyledDiv
            {...attributes}
            style={styles.wrapper}
            // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- TODO: Address a11y error
            tabIndex={0}
            data-math-content={math.tex || ""}
            contentEditable={false}
        >
            <StyledDiv style={styles.inlineBlock}>
                <Popover
                    opened
                    dismissEnabled
                    content={
                        <PopoverContentCore style={styles.popover}>
                            <StyledTextarea
                                id="coeditor-block-math-tex-editor"
                                value={editedTeX}
                                onChange={(e) => setEditedTeX(e.target.value)}
                                style={styles.textArea}
                                aria-label={t`math expression editor`}
                                rows={numRows}
                            />
                        </PopoverContentCore>
                    }
                >
                    {div}
                </Popover>
            </StyledDiv>
        </StyledDiv>
    );
}

const styles = StyleSheet.create({
    wrapper: {
        display: "block",
        marginBlockEnd: spacing.medium_16 + spacing.xxSmall_6,
    },
    focused: {
        outline: `1px solid ${color.blue}`,
        outlineOffset: spacing.xxxxSmall_2,
        borderRadius: spacing.xxxSmall_4,
    },
    selected: {
        backgroundColor: color.fadedPurple8,
    },
    math: {
        display: "inline-block",
        color: "inherit",
        lineHeight: "1em",
    },
    inlineBlock: {
        display: "inline-block",
        cursor: "pointer",
    },
    popover: {
        padding: spacing.xSmall_8,
    },
    textArea: {
        padding: spacing.xSmall_8,
        border: `1px solid ${color.offBlack16}`,
        borderRadius: spacing.xxxSmall_4,
        textAlign: "start",
        font: "inherit",
        whiteSpace: "pre",
        resize: "none",
        width: "minmax(100px, fit)",
        display: "block",
        boxSizing: "border-box",
        ":focus": {
            outline: "none",
            borderColor: color.blue,
        },
    },
});
